import { useEffect, useRef } from "react";
import { can } from "@/lib/rbac";
import { useGanttWindow } from "./gantt/useGanttWindow";
import { useGanttDrag } from "./gantt/useGanttDrag";
import { GanttToolbar, GanttHeaderRow, GanttLegend } from "./gantt/GanttChrome";
import { GanttFocusBanner } from "./gantt/GanttFocusBanner";
import { GanttRow } from "./gantt/GanttRow";

const UNASSIGNED_ROW = { id: "none", line_code: "—", name: "Unassigned", max_hourly_capacity: 0 };

export default function GanttTimeline({ orders, machines, role, onReschedule, activeDrags = {}, onDragPresence, downtimes = [], workingDays, shortages = {}, onSelectOrder, focusLineId = null, onClearFocus, hideUnassigned = false }) {
  const canDrag = can.manageOrders(role);
  const win = useGanttWindow(orders);
  const drag = useGanttDrag({ canDrag, days: win.days, cols: win.cols, orders, onReschedule });

  const rows = hideUnassigned ? machines : [...machines, UNASSIGNED_ROW];
  const focusLine = focusLineId ? rows.find((r) => r.id === focusLineId) : null;
  const visibleRows = focusLine ? [focusLine] : rows;
  const ref = useRef(null);
  useEffect(() => { if (focusLineId) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [focusLineId]);

  return (
    <div ref={ref} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="gantt-timeline-container">
      <GanttToolbar canDrag={canDrag} days={win.days} onSetDays={win.setDays} onMove={win.move} onToday={win.today} />
      {focusLine && <GanttFocusBanner line={focusLine} onClear={onClearFocus} />}
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          <GanttHeaderRow cols={win.cols} days={win.days} workingDays={workingDays} />
          {visibleRows.map((line) => (
            <GanttRow
              key={line.id}
              line={line}
              orders={orders}
              downtimes={downtimes}
              cols={win.cols}
              days={win.days}
              winStart={win.winStart}
              winEnd={win.winEnd}
              workingDays={workingDays}
              canDrag={canDrag}
              dragOver={drag.dragOver}
              activeDrags={activeDrags}
              shortages={shortages}
              onDragOver={drag.handleDragOver(line.id)}
              onDragLeave={drag.handleDragLeave(line.id)}
              onDrop={drag.handleDrop(line.id)}
              onDragPresence={onDragPresence}
              onSelectOrder={onSelectOrder}
            />
          ))}
        </div>
      </div>
      <GanttLegend />
    </div>
  );
}
